import { useDispatch, useSelector } from "react-redux";
import CheckoutItem from "../../components/checkout-item/checkout-item.component";
import {
  selectCartItems,
  selectCustomerAccountCache,
  selectCartNotDiscountTotal,
  selectCartTotal,
  selectCustomerDetails,
  selectDeliveryAddress,
  selectDiscount,
  selectScheduledTime,
} from "../../store/cart/cart.selector";
import {
  selectKitchen,
  selectOnlineOrdersConfigs,
} from "../../store/kitchen/kitchen.selector";
import "./checkout.styles.jsx";
import {
  BottomContainer,
  CheckoutContainer,
  DetailsTitle,
  PickupDetailsContainer,
  Title,
  TotalTextSm,
  TotalTextLg,
  TotalContainer,
  PickupDetailsItem,
  TextLarge,
  TextSmall,
  TotalItem,
} from "./checkout.styles.jsx";
import { BUTTON_TYPE_CLASSES } from "../../components/button/button.component";
import PaymentButton from "../../components/payment-button/payment-button.component";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CheckoutDetails from "../../components/checkout-details/checkout-details.component";
import { logEvent } from "firebase/analytics";
import { analytics, functions } from "../../utils/firebase/firebase.utils";
import { httpsCallable } from "firebase/functions";
import { Box, useMediaQuery } from "@mui/material";
import { preWarmTyro } from "../../utils/tyro/prewarmTyro.utils";
import OrderTypeBottomSheet from "../../components/order-type-bottom-sheet/order-type-bottom-sheet.component";
import {
  formatScheduledPickupTime,
  toDateFromScheduledValue,
  getColor,
  getFontName,
} from "../../utils/utils.common";
import OrderNoteBottomSheet from "../../components/order-note-bottom-sheet/order-note-bottom-sheet.component";
import OrderCustomerDetailsBottomSheet from "../../components/order-customer-details-bottom-sheet/order-customer-details-bottom-sheet.component";
import { roundedUp } from "../../utils/roundUpNumber";
import ButtonAction from "../../components/shared/buttonAction/buttonAction.component";
import DiscountView from "./discount.component";
import {
  CheckoutItemAction,
  CheckoutItemContainer,
  HeaderItem,
  HeaderText,
  PriceText,
} from "../../components/checkout-item/checkout-item.styles";
import {
  clearCustomerAccountCache,
  clearDiscount,
  clearNewCustomer,
  setNewCustomer,
} from "../../store/cart/cart.action";
import { ensureSurchargeConfigsAsync, fetchKitchenAsync } from "../../store/kitchen/kitchen.action";
import EmptyState from "../../components/shared/emptyState/emptyState.component";
import {
  calculateHolidaySurcharge,
  getActiveSurcharge,
} from "../../utils/surcharges";
import { getCartLineAvailabilityMessages } from "../../utils/cartLineAvailability";
import { fieldError } from "../../constants";
import { resolveOrderType } from "../../utils/orderType";
import {
  formatDeliveryAddress,
  isDeliveryAddressSet,
} from "../../utils/deliveryAddress";
import { resolveDeliveryFeeAmount, getDeliveryUnavailableMessage, isDeliveryOrderingAvailable } from "../../utils/deliveryConfig";

const Checkout = () => {
  const kitchen = useSelector(selectKitchen);
  const cartItems = useSelector(selectCartItems);
  const cartSubtotal = useSelector(selectCartTotal);
  const cartNotDiscountTotal = useSelector(selectCartNotDiscountTotal);
  const discountData = useSelector(selectDiscount);
  const customerAccountCache = useSelector(selectCustomerAccountCache);

  const searchParams = new URLSearchParams(window.location.search);
  const tableNumber = searchParams.get("tableNumber");
  const deliveryAddress = useSelector(selectDeliveryAddress);

  const navigate = useNavigate();

  const getScheduledTime = useSelector(selectScheduledTime);
  const formattedScheduledTime = formatScheduledPickupTime(getScheduledTime, kitchen?.timezone);
  const deliveryMinimumTime =
    kitchen?.deliveryConfig?.deliveryMinimumTime || 15;
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const getOrderType = resolveOrderType(searchParams, onlineOrdersConfig);
  const isDeliveryAvailable = isDeliveryOrderingAvailable(
    kitchen?.deliverySchedule,
    onlineOrdersConfig?.onlineDeliveryPaused,
    kitchen?.timezone
  );
  const deliveryUnavailableMessage = getDeliveryUnavailableMessage(
    kitchen?.deliverySchedule,
    onlineOrdersConfig?.onlineDeliveryPaused,
    kitchen?.timezone
  );
  const deliveryFee =
    getOrderType === "delivery"
      ? resolveDeliveryFeeAmount(kitchen, cartSubtotal)
      : 0;

  const cardFeePercent = onlineOrdersConfig?.cardFeePercent
    ? onlineOrdersConfig?.cardFeePercent / 100
    : 0;
  const cardFeeFixedCharge = onlineOrdersConfig?.cardFeeFixedCharge
    ? onlineOrdersConfig?.cardFeeFixedCharge
    : 0;

  const activeSurcharge = getActiveSurcharge(onlineOrdersConfig, kitchen?.timezone);
  const holidaySurcharge = roundedUp(
    calculateHolidaySurcharge(cartSubtotal, activeSurcharge)
  );
  const baseBeforeCardSurcharge = roundedUp(
    Number(cartSubtotal) + Number(holidaySurcharge) + Number(deliveryFee)
  );
  const cardTotalFee = roundedUp(
    baseBeforeCardSurcharge * cardFeePercent + cardFeeFixedCharge
  );
  const cartTotal = roundedUp(
    Number(baseBeforeCardSurcharge) + Number(cardTotalFee)
  );

  const [showOrderTypeBottomSheet, setShowOrderTypeBottomSheet] =
    useState(false);
  const [orderTypeError, setOrderTypeError] = useState(false);
  const [preFetchedTyro, setPreFetchedTyro] = useState(null);
  const hasPreFetchedRef = useRef(false);
  const hasPreWarmedTyroRef = useRef(false);
  const hasAttemptedCheckoutSurchargeFetchRef = useRef(false);
  const lastCartTotalRef = useRef(cartTotal);
  const abortControllerRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const PickupDetailsRef = useRef(null);

  const { kitchenSlug } = useParams();
  const { pathname } = useLocation();

  const isMobile = useMediaQuery("(max-width: 768px)");

  const dispatch = useDispatch();

  const [cartAvailabilityTick, setCartAvailabilityTick] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCartAvailabilityTick((t) => t + 1);
    }, 60000);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setCartAvailabilityTick((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const scheduledPickupAt = useMemo(() => {
    if (getOrderType !== "takeaway" && getOrderType !== "delivery") return null;
    return toDateFromScheduledValue(getScheduledTime);
  }, [getOrderType, getScheduledTime]);

  const cartLineAvailabilityMessages = useMemo(
    () =>
      getCartLineAvailabilityMessages(kitchen, cartItems, {
        scheduledPickupAt,
      }),
    [kitchen, cartItems, cartAvailabilityTick, scheduledPickupAt]
  );
  const hasInvalidCheckoutLines = cartLineAvailabilityMessages.size > 0;

  const groupedCartItems = useMemo(() => {
    if (!cartItems || cartItems.length === 0) return [];

    const groups = [];
    const processedChildren = new Set();
    const bundleGroupMap = new Map();

    const serializeVariants = (variants = []) =>
      variants
        .map(
          (variant) =>
            `${variant?.optionCategory || ""}:${variant?.name || ""}:${
              Number(variant?.charge) || 0
            }:${variant?.extraCharge ? 1 : 0}`
        )
        .sort()
        .join("|");

    const buildBundleGroupKey = (parent, children) => {
      const orderedChildren = [...children].sort((a, b) =>
        `${a?.bundleStepId || ""}-${a?.dishName || ""}`.localeCompare(
          `${b?.bundleStepId || ""}-${b?.dishName || ""}`
        )
      );

      const childSignature = orderedChildren
        .map(
          (child) =>
            `${child?.bundleStepId || ""}:${child?.id || ""}:${
              child?.dishName || ""
            }:${Number(child?.priceAdjustment) || 0}:${child?.note || ""}:${serializeVariants(
              child?.variantSelected || []
            )}`
        )
        .join("||");

      return `${parent?.bundleTemplateId || parent?.id || parent?.dishName || ""}::${childSignature}`;
    };

    cartItems.forEach((item) => {
      if (item.bundleParentId) return;

      if (item.isBundle === true) {
        const children = cartItems.filter(
          (child) => child.bundleParentId === item.cartItemId
        );
        children.forEach((child) => processedChildren.add(child.cartItemId));
        const bundleGroupKey = buildBundleGroupKey(item, children);
        const existingBundleGroup = bundleGroupMap.get(bundleGroupKey);

        if (existingBundleGroup) {
          existingBundleGroup.bundleAggregate.parentIds.push(item.cartItemId);
          existingBundleGroup.bundleAggregate.instanceCount +=
            Number(item.quantity) || 1;
          existingBundleGroup.bundleAggregate.totalPrice +=
            Number(item.dishPriceWithVariants || 0) * Number(item.quantity || 1);
          return;
        }

        const newBundleGroup = {
          type: "bundle",
          parent: item,
          children,
          bundleAggregate: {
            parentIds: [item.cartItemId],
            instanceCount: Number(item.quantity) || 1,
            totalPrice:
              Number(item.dishPriceWithVariants || 0) * Number(item.quantity || 1),
          },
        };

        bundleGroupMap.set(bundleGroupKey, newBundleGroup);
        groups.push(newBundleGroup);
        return;
      }

      groups.push({ type: "regular", item });
    });

    cartItems.forEach((item) => {
      if (item.bundleParentId && !processedChildren.has(item.cartItemId)) {
        groups.push({ type: "regular", item });
      }
    });

    return groups;
  }, [cartItems]);

  useEffect(() => {
    dispatch(clearNewCustomer());
  }, [dispatch]);

  useEffect(() => {
    if (!kitchenSlug) return;
    dispatch(fetchKitchenAsync(`/${kitchenSlug}/checkout`));
  }, [dispatch, kitchenSlug]);

  useEffect(() => {
    if (!kitchen?.kitchenId) return;
    if (onlineOrdersConfig?.surchargeConfigs) {
      hasAttemptedCheckoutSurchargeFetchRef.current = false;
      return;
    }
    if (hasAttemptedCheckoutSurchargeFetchRef.current) return;

    hasAttemptedCheckoutSurchargeFetchRef.current = true;
    dispatch(
      ensureSurchargeConfigsAsync(kitchen.kitchenId, onlineOrdersConfig || null)
    );
  }, [dispatch, kitchen?.kitchenId, onlineOrdersConfig]);

  useEffect(() => {
    logEvent(analytics, "checkout_visited", {
      kitchenName: kitchen?.kitchenName,
    });
  }, [kitchen]);

  useEffect(() => {
    if (
      cartItems.length > 0 &&
      onlineOrdersConfig?.tyroLocationId &&
      !hasPreWarmedTyroRef.current
    ) {
      hasPreWarmedTyroRef.current = true;
      preWarmTyro();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length, onlineOrdersConfig?.tyroLocationId]);

  useEffect(() => {
    if (
      cartItems.length > 0 &&
      onlineOrdersConfig?.tyroLocationId
    ) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      
      const cartTotalChanged = lastCartTotalRef.current !== cartTotal;
      
      if (cartTotalChanged && hasPreFetchedRef.current) {
        hasPreFetchedRef.current = false;
        lastCartTotalRef.current = cartTotal;
      }
      
      if (!hasPreFetchedRef.current) {
        fetchTimeoutRef.current = setTimeout(() => {
          hasPreFetchedRef.current = true;
          abortControllerRef.current = new AbortController();
          const currentAbortController = abortControllerRef.current;
          const currentCartTotal = cartTotal;
          
          const fetchPaySecret = async () => {
            try {
              const callFunction = httpsCallable(
                functions,
                "tyroEndpoints-createOrder"
              );

              const response = await callFunction({
                cartTotal: currentCartTotal,
                tyroLocationID: onlineOrdersConfig?.tyroLocationId,
              });

              if (currentAbortController.signal.aborted) {
                return;
              }

              if (response.data.statusCode === 200) {
                setPreFetchedTyro({
                  paySecret: response.data.paySecret,
                  payRequestId: response.data?.payRequestId || null,
                  orderId: response.data?.orderId || null,
                  cartTotal: currentCartTotal,
                });
                lastCartTotalRef.current = currentCartTotal;
              }
            } catch (error) {
              if (error?.name === 'AbortError' || currentAbortController.signal.aborted) {
                return;
              }
              console.error("Failed to pre-fetch paySecret:", error);
            } finally {
              if (abortControllerRef.current === currentAbortController) {
                abortControllerRef.current = null;
              }
            }
          };

          fetchPaySecret();
        }, 300);
      }
    }
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length, onlineOrdersConfig?.tyroLocationId, cartTotal]);

  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );
  const brandAccent = getColor("brand-accent", onlineOrdersConfig);
  const brandFontStyle = getFontName("brand-font-style", onlineOrdersConfig);
  const backgroundTertiary = getColor(
    "background-tertiary",
    onlineOrdersConfig
  );
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const brandTertiary = getColor("brand-tertiary", onlineOrdersConfig);

  const childRef = useRef(null);

  const setNewCustomerState = async () => {
    if (!onlineOrdersConfig.customerCreationEnabled) {
      dispatch(clearNewCustomer());
      return;
    }

    if (!childRef.current.addCustomer) {
      dispatch(clearCustomerAccountCache());
      dispatch(clearNewCustomer());
      return;
    }

    const formEmail = childRef.current.formFields.customerEmail
      ?.trim()
      .toLowerCase();
    const formMobile = childRef.current.formFields.customerMobile?.trim();
    const formMobileDigits = (formMobile || "").replace(/\D/g, "");
    const identityKey = `${formEmail || ""}|${formMobileDigits}`;

    if (
      customerAccountCache &&
      (customerAccountCache.kitchenId !== kitchen.kitchenId ||
        customerAccountCache.identityKey !== identityKey)
    ) {
      dispatch(clearCustomerAccountCache());
    }

    const formName = childRef.current.formFields.customerName || "";
    const [firstName, ...lastNameParts] = formName.trim().split(" ");
    const lastName = lastNameParts.join(" ");
    const hasIdentity = Boolean(formEmail || formMobileDigits);
    if (!hasIdentity) {
      dispatch(clearNewCustomer());
      return;
    }

    const createCustomerModel = {
      firstName,
      lastName,
      email: formEmail,
      mobileNumber: formMobile,
      creationSource: "online",
      createdAt: new Date(),
      marketingOffersConsent: childRef.current.addCustomer,
      kitchenId: kitchen.kitchenId,
    };
    dispatch(setNewCustomer(createCustomerModel));
  };

  const triggerSubmit = async () => {
    if (hasInvalidCheckoutLines) {
      return;
    }
    if (childRef.current) {
      const isValid = await childRef.current.handleSubmit();

      if (isValid) {
        setNewCustomerState();
        const currentParams = new URLSearchParams(window.location.search);
        navigate(
          `/${kitchenSlug}/checkout/payment?${currentParams.toString()}`,
          {
            state: preFetchedTyro ? { tyro: preFetchedTyro } : undefined,
          }
        );
      }
    }
  };

  if (cartItems.length === 0) {
    return (
      <CheckoutContainer>
        {!isMobile && (
          <Title brandFontStyle={brandFontStyle} brandAccent={brandAccent}>
            Your Cart
          </Title>
        )}
        <EmptyState
          largeText="Your cart is empty."
          normalText="Go back to the menu and add some items."
          activeBgHeaderModal
          backButton
        />
      </CheckoutContainer>
    );
  } else {
    return (
      <Box
        sx={{
          backgroundColor: headerModalBackground,
          minHeight: "calc(100vh - 72px)",
          "@media screen and (max-width: 768px)": {
            minHeight: "calc(100vh - 64px)",
          },
        }}
      >
        <CheckoutContainer>
          {!isMobile && (
            <Title brandFontStyle={brandFontStyle} brandAccent={brandAccent}>
              Your Cart
            </Title>
          )}
          <PickupDetailsContainer>
            {kitchen.fullAddress && (
              <PickupDetailsItem ref={PickupDetailsRef}>
                {getOrderType === "takeaway" && (
                  <CheckoutDetails
                    onClick={() =>
                      setShowOrderTypeBottomSheet(!showOrderTypeBottomSheet)
                    }
                    title="Take Away"
                    body={`${
                      formattedScheduledTime === null
                        ? `Pick up ASAP (Ready in ${onlineOrdersConfig?.orderReadyTime} Mins)`
                        : `Pick Up ${formattedScheduledTime}`
                    }`}
                  />
                )}
                {getOrderType === "dinein" && (
                  <CheckoutDetails
                    onClick={() =>
                      setShowOrderTypeBottomSheet(!showOrderTypeBottomSheet)
                    }
                    title="Dine In"
                    body={`${
                      tableNumber
                        ? `Table ${tableNumber}`
                        : "Enter Table Number"
                    }`}
                    error={orderTypeError ? "Enter Table Number" : ""}
                  />
                )}
                {getOrderType === "delivery" && (
                  <CheckoutDetails
                    onClick={() =>
                      setShowOrderTypeBottomSheet(!showOrderTypeBottomSheet)
                    }
                    title="Delivery"
                    body={
                      !isDeliveryAvailable ? (
                        deliveryUnavailableMessage
                      ) : isDeliveryAddressSet(deliveryAddress) ? (
                        <>
                          <div>
                            {formattedScheduledTime === null
                              ? `Deliver ASAP in ${deliveryMinimumTime} mins`
                              : `Deliver ${formattedScheduledTime}`}
                          </div>
                          <div>{formatDeliveryAddress(deliveryAddress)}</div>
                        </>
                      ) : (
                        "Enter delivery address"
                      )
                    }
                    error={
                      orderTypeError
                        ? !isDeliveryAvailable
                          ? deliveryUnavailableMessage
                          : "Enter delivery address"
                        : ""
                    }
                  />
                )}
                <ButtonAction
                  onClick={() =>
                    setShowOrderTypeBottomSheet(!showOrderTypeBottomSheet)
                  }
                  size="medium"
                  label="Change"
                  backgroundTertiary={backgroundTertiary}
                  brandPrimary={brandPrimary}
                />
              </PickupDetailsItem>
            )}
            <PickupDetailsItem>
              <div>
                <TextLarge>{kitchen.kitchenName}</TextLarge>
                <TextSmall>{kitchen.fullAddress}</TextSmall>
              </div>
            </PickupDetailsItem>
            <DiscountView discountName={discountData?.name} />
          </PickupDetailsContainer>
          {groupedCartItems.map((group) =>
            group.type === "bundle" ? (
              <CheckoutItem
                key={group.parent.cartItemId}
                cartItem={group.parent}
                bundleChildren={group.children}
                bundleAggregate={group.bundleAggregate}
                availabilityMessages={cartLineAvailabilityMessages}
              />
            ) : (
              <CheckoutItem
                key={group.item.cartItemId}
                cartItem={group.item}
                availabilityMessages={cartLineAvailabilityMessages}
              />
            )
          )}
          {discountData && (
            <CheckoutItemContainer backgroundTertiary={backgroundTertiary}>
              <HeaderItem>
                <HeaderText>
                  Discount {discountData.type === "amount" ? "$" : ""}
                  {discountData.value}
                  {discountData.type === "percentage" ? "%" : ""} Off
                </HeaderText>

                <PriceText>
                  $
                  {discountData.type === "percentage"
                    ? Number(
                        (cartNotDiscountTotal * discountData.value) / 100
                      )?.toFixed(2)
                    : discountData.type === "amount"
                    ? Number(discountData?.value)?.toFixed(2)
                    : "0.00"}
                </PriceText>
              </HeaderItem>

              <TotalItem>
                <TotalTextSm style={{ marginBottom: 0 }}>
                  Code: {discountData.name || "N/A"}
                </TotalTextSm>
              </TotalItem>
              {pathname === `/${kitchenSlug}/checkout` && (
                <CheckoutItemAction style={{ justifyContent: "flex-end" }}>
                  <ButtonAction
                    onClick={() => dispatch(clearDiscount())}
                    label="Remove"
                    backgroundTertiary={backgroundTertiary}
                    brandPrimary={brandTertiary}
                  />
                </CheckoutItemAction>
              )}
            </CheckoutItemContainer>
          )}
          <TotalContainer>
            <TotalItem>
              <TotalTextSm>Subtotal</TotalTextSm>
              <TotalTextSm>${cartSubtotal.toFixed(2)}</TotalTextSm>
            </TotalItem>
            {activeSurcharge?.enabled && holidaySurcharge !== 0 && (
              <TotalItem>
                <TotalTextSm>{activeSurcharge?.name} Surcharge</TotalTextSm>
                <TotalTextSm>${Number(holidaySurcharge)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            {deliveryFee > 0 && (
              <TotalItem>
                <TotalTextSm>Delivery Fee</TotalTextSm>
                <TotalTextSm>${Number(deliveryFee)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            {cardTotalFee !== 0 && (
              <TotalItem>
                <TotalTextSm>Card Surcharge</TotalTextSm>
                <TotalTextSm>${Number(cardTotalFee)?.toFixed(2)}</TotalTextSm>
              </TotalItem>
            )}
            <TotalItem>
              <TotalTextLg>Total (Including GST)</TotalTextLg>
              <TotalTextLg>${cartTotal?.toFixed(2)}</TotalTextLg>
            </TotalItem>
          </TotalContainer>
          <Box
            sx={{
              padding: "0 16px 32px",
              borderBottom: `4px solid ${backgroundTertiary}`,
              "@media screen and (max-width: 768px)": {
                padding: "0 16px 24px",
              },
            }}
          >
            <OrderNoteBottomSheet />
          </Box>
          <Box
            sx={{
              margin: "32px 16px",
              "@media screen and (max-width: 768px)": {
                margin: "24px 16px 0 16px",
              },
            }}
          >
            <DetailsTitle>Your Details</DetailsTitle>
            <OrderCustomerDetailsBottomSheet ref={childRef} />
          </Box>
        </CheckoutContainer>

        <BottomContainer
          headerModalBackground={headerModalBackground}
          backgroundTertiary={backgroundTertiary}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: "494px",
              margin: "0 auto",
              gap: 1,
            }}
          >
            {hasInvalidCheckoutLines && (
              <Box
                sx={{
                  px: 2,
                  width: "100%",
                  color: fieldError,
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Remove unavailable items to continue.
              </Box>
            )}
            <Box
              sx={{
                width: "100%",
                opacity: hasInvalidCheckoutLines ? 0.55 : 1,
                transition: "opacity 0.2s ease",
                pointerEvents: hasInvalidCheckoutLines ? "none" : "auto",
              }}
            >
              <PaymentButton
                buttonType={BUTTON_TYPE_CLASSES.cart}
                onClick={async () => {
                  if (hasInvalidCheckoutLines) {
                    return;
                  }
                  if (
                    (tableNumber === null || tableNumber === "") &&
                    getOrderType === "dinein"
                  ) {
                    setOrderTypeError(true);
                    if (PickupDetailsRef.current) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  } else if (getOrderType === "delivery") {
                    if (!isDeliveryAvailable || !isDeliveryAddressSet(deliveryAddress)) {
                      setOrderTypeError(true);
                      setShowOrderTypeBottomSheet(true);
                      if (PickupDetailsRef.current) {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    } else {
                      await triggerSubmit();
                    }
                  } else {
                    await triggerSubmit();
                  }
                }}
                style={{ flex: 1, maxWidth: "100%" }}
                text="Continue to Payment"
                disabled={hasInvalidCheckoutLines}
              />
            </Box>
          </Box>
        </BottomContainer>

        <OrderTypeBottomSheet
          isOpen={showOrderTypeBottomSheet}
          toggleBottomSheet={() => setShowOrderTypeBottomSheet(false)}
          onlineOrdersConfig={onlineOrdersConfig}
          setOrderTypeError={setOrderTypeError}
          tableNumber={tableNumber}
          orderType={getOrderType}
        />
      </Box>
    );
  }
};

export default Checkout;
